import React, {useEffect, useState} from 'react'
import {Accordion, AccordionButton, AccordionItem, AccordionPanel, Box,} from '@chakra-ui/react'
import {FormProvider, useForm} from 'react-hook-form'
import {cloneDeep, isEmpty, round} from 'lodash-es'
import {AddIcon, MinusIcon} from '@chakra-ui/icons'
import {useTranslation} from 'next-i18next'
import {ProductAttrKey} from '_app/types/product'
import useAttributesMetadata from '_app/hooks/useAttributesMetadata'
import Range from '_app/components/Form/Range'
import Badge from '_app/components/Badge'
import {useRouter} from '_app/hooks/useRouter'
import MobileActions from '../MobileActions'
import SortMobile from '../SortMobile'
import {DropdownLayerFilter} from './FiltersDropdown'
import FilterItemsList from './FilterItemsList'
import FiltersFormCounter from './FiltersFormCounter'
import FilterEnum from '../../../enums/FilterEnum/FilterEnum'
import AttributesEnum from '../../../enums/AttributesEnum/AttributesEnum'
import URLHelper from '../../../helpers/URLHelper/URLHelper'

type LoadingState = 'clear' | 'submit' | undefined;

interface MobileFiltersFormProps {
  defaultValues: { [key: string]: any };
  initialValues: { [key: string]: any };
  filters: DropdownLayerFilter[];
  minPrice: number;
  maxPrice: number;
  active: string;
  onClose: () => void;
}

const MobileFiltersForm = ({
  active,
  defaultValues,
  initialValues,
  filters,
  minPrice,
  maxPrice,
  onClose,
}: MobileFiltersFormProps) => {
  const router = useRouter()
  const { t } = useTranslation('listing')

  const [loadingState, setLoadingState] = useState<LoadingState>(undefined)

  const methods = useForm({
    defaultValues: cloneDeep(defaultValues),
  })

  const { reset, handleSubmit, getValues } = methods

  useEffect(() => {
    reset(cloneDeep(initialValues))
  }, [reset, initialValues])

  const onSubmit = async (data: { [key: string]: any }) => {
    setLoadingState('submit')
    const query: { [key: string]: any } = URLHelper.getSearchParams()

    AttributesEnum.FILTERS_ATTRIBUTE_CODES.forEach(key => {
      delete query[key]
    })

    const { price, ...restValues } = data

    if (price && (price[0] !== minPrice || price[1] !== maxPrice)) {
      query.price = price.join('-')
    }

    Object.entries(restValues).map(([key, value]) => {
      if (!isEmpty(value)) {
        query[key] = value
      }
    })

    await router.search(query)

    setLoadingState(undefined)
    onClose()
  }

  const onClear = async () => {
    setLoadingState('clear')
    const query = URLHelper.getSearchParams()

    AttributesEnum.FILTERS_ATTRIBUTE_CODES.forEach(key => {
      delete query[key]
    })

    await router.search(query)

    reset(cloneDeep(defaultValues))

    setLoadingState(undefined)
    onClose()
  }

  const { metaData: attributes } = useAttributesMetadata({})

  const getAttributeCount = (attributeCode: string) => {
    const value = getValues(attributeCode)
    if (attributeCode === 'price') {
      if (
        parseInt(value[0], 0) !== minPrice ||
        parseInt(value[1], 0) !== maxPrice
      ) {
        return 1
      }

      return 0
    }

    return value?.length
  }

  return (
    <>
      <FormProvider {...methods}>
        <Box
          as="form"
          id="filters-mobile"
          flex={1}
          overflow="hidden"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box
            height="100%"
            overflowY="auto"
            mt={-2}
            pb={{ base: '4.125rem', lg: 'unset' }}
          >
            <Accordion allowMultiple borderColor="transparent">
              <AccordionItem key="sort">
                {({ isExpanded }) => (
                  <>
                    <AccordionButton
                      border="none"
                      mb={2}
                      _expanded={{ bg: 'transparent' }}
                      _hover={{ bg: 'transparent' }}
                      _active={{ bg: 'transparent' }}
                    >
                      <Box flex="1" textAlign="left" fontWeight={400}>
                        <Badge inline>{t('SORT')}</Badge>
                      </Box>
                      {isExpanded ? (
                        <MinusIcon fontSize="0.75rem" />
                      ) : (
                        <AddIcon fontSize="0.75rem" />
                      )}
                    </AccordionButton>

                    <AccordionPanel pb={0}>
                      <SortMobile active={active} />
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
              {filters
                .filter(f =>
                  FilterEnum.ALLOWED_FILTER_ATTRIBUTE_CODES.includes(f.attribute_code)
                )
                .map(({ label, attribute_code, options }) => (
                  <AccordionItem key={`fm-${attribute_code}`}>
                    {({ isExpanded }) => (
                      <>
                        <AccordionButton
                          border="none"
                          mb={2}
                          _expanded={{ bg: 'transparent' }}
                          _hover={{ bg: 'transparent' }}
                          _active={{ bg: 'transparent' }}
                        >
                          <Box flex="1" textAlign="left" fontWeight={400}>
                            <Badge
                              value={
                                !isExpanded && getAttributeCount(attribute_code)
                              }
                              inline
                            >
                              {label}
                            </Badge>
                          </Box>
                          {isExpanded ? (
                            <MinusIcon fontSize="0.75rem" />
                          ) : (
                            <AddIcon fontSize="0.75rem" />
                          )}
                        </AccordionButton>

                        <AccordionPanel pb={0}>
                          {attribute_code === 'price' ? (
                            <Box maxW="calc(100% - 5rem)" pb={6}>
                              <Range
                                name={attribute_code}
                                orientation="horizontal"
                                extend={40}
                                min={minPrice}
                                max={maxPrice}
                                minStepsBetweenThumbs={
                                  round(
                                    (defaultValues?.price[1] -
                                      defaultValues?.price[0]) /
                                      5,
                                    0
                                  ) || 0
                                }
                              />
                            </Box>
                          ) : (
                            <FilterItemsList
                              requestVar={attribute_code}
                              items={options}
                              swatches={
                                attributes[attribute_code as ProductAttrKey]
                              }
                            />
                          )}
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                ))}
            </Accordion>
          </Box>
        </Box>
        <Box position="fixed" bottom={0} left={0} right={0} zIndex={1}>
          <MobileActions
            onClear={onClear}
            formId="filters-mobile"
            loadingState={loadingState}
            count={
              <FiltersFormCounter
                {...{
                  minPrice,
                  maxPrice,
                }}
              />
            }
          />
        </Box>
      </FormProvider>
    </>
  )
}

export default MobileFiltersForm
